import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useCondominio } from '../contexts/CondominioContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { Calendar, ChevronLeft, ChevronRight, Clock, MapPin, Users, Info, X } from 'lucide-react';

const Agenda = () => {
  const { user } = useAuth();
  const { getCondominioId, isMorador, isAdministrador } = useCondominio();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [eventosDoDia, setEventosDoDia] = useState([]);
  const [showEventoModal, setShowEventoModal] = useState(false);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [showNovoEventoModal, setShowNovoEventoModal] = useState(false);
  const [formData, setFormData] = useState({
    agenda_titulo: '',
    agenda_descricao: '',
    agenda_tipo: 'evento',
    agenda_data_inicio: '',
    agenda_data_fim: '',
    agenda_local: '',
    agenda_organizador: '',
    agenda_contato: '',
    agenda_observacoes: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadEventos();
  }, [currentDate]);

  const loadEventos = async () => {
    try {
      setLoading(true);
      const condominioId = getCondominioId();
      if (!condominioId) return;

      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const response = await api.get(`/agenda-condominio?condominio_id=${condominioId}&data_inicio=${startDate.toISOString()}&data_fim=${endDate.toISOString()}`);
      setEventos(response.data.data.eventos || []);
    } catch (error) {
      console.error('Erro ao carregar eventos:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Adicionar dias vazios do mês anterior
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Adicionar dias do mês atual
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const getEventosDoDia = (date) => {
    if (!date) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    return eventos.filter(evento => {
      const eventoDate = new Date(evento.agenda_data_inicio).toISOString().split('T')[0];
      return eventoDate === dateStr;
    });
  };

  const handleDateClick = (date) => {
    if (!date) return;
    
    setSelectedDate(date);
    const eventosDia = getEventosDoDia(date);
    setEventosDoDia(eventosDia);
    
    // Se há eventos, mostrar o modal
    if (eventosDia.length > 0) {
      setEventoSelecionado(eventosDia[0]); // Mostrar o primeiro evento
      setShowEventoModal(true);
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
    setSelectedDate(null);
    setEventosDoDia([]);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getTipoEventoColor = (tipo) => {
    const colors = {
      'reuniao': 'bg-blue-100 text-blue-800 border-blue-200',
      'evento': 'bg-green-100 text-green-800 border-green-200',
      'manutencao': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'fiscalizacao': 'bg-red-100 text-red-800 border-red-200',
      'social': 'bg-pink-100 text-pink-800 border-pink-200',
      'outros': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return colors[tipo] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getTipoEventoLabel = (tipo) => {
    const labels = {
      'reuniao': 'Reunião',
      'evento': 'Evento',
      'manutencao': 'Manutenção',
      'fiscalizacao': 'Fiscalização',
      'social': 'Social',
      'outros': 'Outros'
    };
    return labels[tipo] || tipo;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      agenda_titulo: '',
      agenda_descricao: '',
      agenda_tipo: 'evento',
      agenda_data_inicio: '',
      agenda_data_fim: '',
      agenda_local: '',
      agenda_organizador: '',
      agenda_contato: '',
      agenda_observacoes: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const condominioId = getCondominioId();
      if (!condominioId) {
        alert('Condomínio não selecionado');
        return;
      }

      const eventData = {
        ...formData,
        condominio_id: condominioId
      };

      await api.post('/agenda-condominio', eventData);
      
      alert('Evento criado com sucesso!');
      setShowNovoEventoModal(false);
      resetForm();
      loadEventos();
    } catch (error) {
      console.error('Erro ao criar evento:', error);
      alert('Erro ao criar evento. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const days = getDaysInMonth(currentDate);
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Calendar className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Agenda do Condomínio</h1>
        </div>
        {isAdministrador() && (
          <button
            onClick={() => setShowNovoEventoModal(true)}
            className="btn btn-primary"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Novo Evento
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-body">
        {/* Navegação do mês */}
        <div className="flex items-center justify-between p-6 border-b">
          <button
            onClick={() => navigateMonth(-1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <h3 className="text-lg font-semibold text-gray-900">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button
            onClick={() => navigateMonth(1)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        {/* Dias da semana */}
        <div className="grid grid-cols-7 gap-1 p-4 border-b">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
              {day}
            </div>
          ))}
        </div>

        {/* Dias do mês */}
        <div className="grid grid-cols-7 gap-1 p-4 mt-4">
          {days.map((day, index) => {
            const dayEventos = day ? getEventosDoDia(day) : [];
            const isToday = day && day.toDateString() === new Date().toDateString();
            const isSelected = selectedDate && day && day.toDateString() === selectedDate.toDateString();
            
            return (
              <div
                key={index}
                className={`
                  min-h-[100px] p-2 border border-gray-200 cursor-pointer hover:bg-gray-50
                  ${!day ? 'bg-gray-50' : ''}
                  ${isToday ? 'bg-blue-50 border-blue-300' : ''}
                  ${isSelected ? 'bg-blue-100 border-blue-400' : ''}
                `}
                onClick={() => handleDateClick(day)}
              >
                {day && (
                  <>
                    <div className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>
                      {day.getDate()}
                    </div>
                    {dayEventos.length > 0 && (
                      <div className="mt-1 space-y-1">
                        {dayEventos.slice(0, 2).map(evento => (
                          <div
                            key={evento.agenda_id}
                            className={`text-xs px-1 py-0.5 rounded border ${getTipoEventoColor(evento.agenda_tipo)}`}
                            title={`${evento.agenda_titulo} - ${formatTime(evento.agenda_data_inicio)}`}
                          >
                            {formatTime(evento.agenda_data_inicio)}
                          </div>
                        ))}
                        {dayEventos.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{dayEventos.length - 2} mais
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </div>

      {/* Modal de detalhes do evento */}
      {showEventoModal && eventoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center space-x-3">
                <Calendar className="h-6 w-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Detalhes do Evento</h2>
              </div>
              <button
                onClick={() => setShowEventoModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conteúdo */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-4">
                {/* Título */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {eventoSelecionado.agenda_titulo}
                  </h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoEventoColor(eventoSelecionado.agenda_tipo)}`}>
                    {getTipoEventoLabel(eventoSelecionado.agenda_tipo)}
                  </span>
                </div>

                {/* Data e Hora */}
                <div className="flex items-center space-x-2 text-gray-600">
                  <Clock className="h-4 w-4" />
                  <span>{formatDate(eventoSelecionado.agenda_data_inicio)} às {formatTime(eventoSelecionado.agenda_data_inicio)}</span>
                </div>

                {/* Local */}
                {eventoSelecionado.agenda_local && (
                  <div className="flex items-center space-x-2 text-gray-600">
                    <MapPin className="h-4 w-4" />
                    <span>{eventoSelecionado.agenda_local}</span>
                  </div>
                )}

                {/* Descrição */}
                {eventoSelecionado.agenda_descricao && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                      <Info className="h-4 w-4 mr-1" />
                      Descrição
                    </h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {eventoSelecionado.agenda_descricao}
                    </p>
                  </div>
                )}

                {/* Organizador */}
                {eventoSelecionado.agenda_organizador && (
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Users className="h-4 w-4" />
                    <span><strong>Organizador:</strong> {eventoSelecionado.agenda_organizador}</span>
                  </div>
                )}

                {/* Contato */}
                {eventoSelecionado.agenda_contato && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Contato</h4>
                    <p className="text-gray-600 text-sm">{eventoSelecionado.agenda_contato}</p>
                  </div>
                )}

                {/* Observações */}
                {eventoSelecionado.agenda_observacoes && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-1">Observações</h4>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {eventoSelecionado.agenda_observacoes}
                    </p>
                  </div>
                )}
              </div>

              {/* Navegação entre eventos do dia */}
              {eventosDoDia.length > 1 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <button
                      onClick={() => {
                        const currentIndex = eventosDoDia.findIndex(e => e.agenda_id === eventoSelecionado.agenda_id);
                        const prevIndex = currentIndex > 0 ? currentIndex - 1 : eventosDoDia.length - 1;
                        setEventoSelecionado(eventosDoDia[prevIndex]);
                      }}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      ← Anterior
                    </button>
                    <span className="text-sm text-gray-500">
                      {eventosDoDia.findIndex(e => e.agenda_id === eventoSelecionado.agenda_id) + 1} de {eventosDoDia.length}
                    </span>
                    <button
                      onClick={() => {
                        const currentIndex = eventosDoDia.findIndex(e => e.agenda_id === eventoSelecionado.agenda_id);
                        const nextIndex = currentIndex < eventosDoDia.length - 1 ? currentIndex + 1 : 0;
                        setEventoSelecionado(eventosDoDia[nextIndex]);
                      }}
                      className="px-3 py-1 text-sm text-blue-600 hover:text-blue-800"
                    >
                      Próximo →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal para criar novo evento */}
      {showNovoEventoModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-gray-900">Novo Evento</h2>
                <button
                  onClick={() => {
                    setShowNovoEventoModal(false);
                    resetForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Título *
                    </label>
                    <input
                      type="text"
                      name="agenda_titulo"
                      value={formData.agenda_titulo}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo de Evento *
                    </label>
                    <select
                      name="agenda_tipo"
                      value={formData.agenda_tipo}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="evento">Evento</option>
                      <option value="reuniao">Reunião</option>
                      <option value="manutencao">Manutenção</option>
                      <option value="fiscalizacao">Fiscalização</option>
                      <option value="social">Social</option>
                      <option value="outros">Outros</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição
                  </label>
                  <textarea
                    name="agenda_descricao"
                    value={formData.agenda_descricao}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Início *
                    </label>
                    <input
                      type="datetime-local"
                      name="agenda_data_inicio"
                      value={formData.agenda_data_inicio}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Data de Fim
                    </label>
                    <input
                      type="datetime-local"
                      name="agenda_data_fim"
                      value={formData.agenda_data_fim}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Local
                    </label>
                    <input
                      type="text"
                      name="agenda_local"
                      value={formData.agenda_local}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organizador
                    </label>
                    <input
                      type="text"
                      name="agenda_organizador"
                      value={formData.agenda_organizador}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contato
                  </label>
                  <input
                    type="text"
                    name="agenda_contato"
                    value={formData.agenda_contato}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                  </label>
                  <textarea
                    name="agenda_observacoes"
                    value={formData.agenda_observacoes}
                    onChange={handleInputChange}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowNovoEventoModal(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50"
                  >
                    {saving ? 'Salvando...' : 'Salvar Evento'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agenda;